import {
  activeSparkDiscount,
  calculateSparkReceipt,
  calculateSparkRegisterFormMissingFields,
  Coupon,
  PublicCredentialRecord,
  SparkDiscount,
  SparkRegisterInfo,
  sparkStatusIsPublished,
  SparkWithRegistrationSummary,
  UserAttributesUpdate,
  UserWithMediaAndGroups,
} from "@greenflagdate/shared";
import { textOptInConsent } from "../../lib/textOptIn";
import { useReq } from "@larner.dev/use-req";
import { useCallback, useMemo, useState } from "react";
import { apiReq } from "../../lib/apiReq";
import { isLoggedIn } from "../../lib/isLoggedIn";
import { trackMetaEvent } from "../../lib/metaPixel";
import { isSparkFullFor } from "../../lib/sparkHelpers";
import { store, useStoreData } from "../../lib/store/store";
import { ActionType } from "../../lib/store/storeActions";

// Wizard step machine. Steps are skipped at entry based on auth + missingFields,
// and the terminal step is decided by goal × free/paid.
export type WizardStep =
  | "email"            // logged-out: collect email + initiate OTP
  | "otp"              // 6-digit code via AuthenticationForm
  | "profile"          // gender + remaining missing fields
  | "waitlist-offer"   // mid-flow switch when profile submit reveals the spark is full for the user's gender
  | "checkout";        // billing for paid sparks; isFree confirm screen otherwise

export type SparkRegistrationView =
  | { kind: "unpublished" }
  | { kind: "wizard"; step: WizardStep; goal: "register" | "waitlist" };

export interface UseSparkRegistrationArgs {
  spark: SparkWithRegistrationSummary;
  onRegister: () => void;
  onJoinWaitlist: () => void;
}

export const useSparkRegistration = ({
  spark,
  onRegister,
  onJoinWaitlist,
}: UseSparkRegistrationArgs) => {
  const storeData = useStoreData();
  const loggedIn = isLoggedIn(storeData);

  // Pending profile values entered during the wizard. After OTP we flush these
  // to the server via saveAttributes; on the profile step they're the form
  // state.
  const [sparkRegisterInfo, setSparkRegisterInfo] = useState<SparkRegisterInfo>(
    {}
  );
  // The text-notification opt-in next to the phone field. Unchecked by
  // default: an affirmative opt-in is what makes the consent valid.
  const [textOptIn, setTextOptIn] = useState(false);
  const [coupon, setCoupon] = useState<Coupon | undefined>(undefined);

  // Mid-flow goal flip: if profile submission reveals the spark is full for
  // the user's gender, the user is offered the waitlist. Acceptance flips
  // `goal` from "register" to "waitlist" and the rest of the flow uses that.
  const [goal, setGoal] = useState<"register" | "waitlist">("register");

  // Step pointer. Computed initially in `view` from auth + missingFields, but
  // user-driven transitions (Continue clicks) move it forward via setStep.
  const [step, setStep] = useState<WizardStep | null>(null);

  const [signupReq, signupReqState] = useReq<PublicCredentialRecord>(apiReq);
  const [waitlistReq, waitlistReqState] = useReq(apiReq);
  const [registerReq, registerReqState] = useReq(apiReq);
  const [saveAttributeReq, saveAttributeReqState] =
    useReq<UserWithMediaAndGroups>(apiReq);

  const [registerLoading, setRegisterLoading] = useState(false);

  const gender = useMemo(
    () => (sparkRegisterInfo.gender || storeData.user?.gender)?.toLowerCase(),
    [storeData.user?.gender, sparkRegisterInfo.gender]
  );

  const activeDiscount = useMemo<SparkDiscount | null>(
    () => activeSparkDiscount({ discount: spark.discount }),
    [spark.discount]
  );

  const effectivePrice = activeDiscount ? activeDiscount.price : spark.price;
  const isFree = effectivePrice === 0;
  // Asked of the receipt, not worked out here: this used to compare the
  // coupon's cents against the price directly, which could not see a
  // percentage coupon at all — a 100% code would have left the card form up
  // asking for money the server was never going to charge. The server decides
  // whether to bill from this same receipt's subtotal.
  const isEffectivelyFree = useMemo(
    () =>
      effectivePrice > 0 &&
      !!coupon &&
      calculateSparkReceipt(spark.price, { discount: activeDiscount, coupon })
        .subtotal === 0,
    [effectivePrice, coupon, spark.price, activeDiscount]
  );

  const missingFields = useMemo(
    () => calculateSparkRegisterFormMissingFields(storeData.user || {}),
    [storeData.user]
  );

  // First step the user should land on. Used to seed `step` whenever it's
  // null (initial render, after a goal flip, etc.). For logged-in users we
  // skip past email/otp; if their profile is complete we skip profile too.
  // Free + complete-profile users land on "checkout" — CheckoutView with
  // isFree renders the dedicated $0 confirm screen. But if the spark is
  // already full for that user's known gender, route to "waitlist-offer"
  // up front so they never see the payment form for a full event.
  const initialStepForRegister = useMemo<WizardStep>(() => {
    if (!loggedIn) {
      return storeData.auth.emailCredentialId ? "otp" : "email";
    }
    if (missingFields.length > 0) return "profile";
    if (isSparkFullFor(gender, spark)) return "waitlist-offer";
    return "checkout";
  }, [loggedIn, storeData.auth.emailCredentialId, missingFields.length, gender, spark]);

  const currentStep: WizardStep = step ?? initialStepForRegister;

  const view: SparkRegistrationView = useMemo(() => {
    // An event members cannot see — a Draft, or a Tentative date — has no
    // registration to offer; only an admin previewing it gets this far.
    if (!sparkStatusIsPublished(spark.status)) return { kind: "unpublished" };
    return { kind: "wizard", step: currentStep, goal };
  }, [spark.status, currentStep, goal]);

  // ---- Actions ---------------------------------------------------------

  const initiateLogin = useCallback(
    async (email: string) => {
      const response = await signupReq.post("/user", { email });
      if (response.success) {
        store.dispatch({
          type: ActionType.UpdateAuth,
          params: { emailCredentialId: response.result.id },
        });
        setSparkRegisterInfo((prev) => ({ ...prev, email }));
        setStep("otp");
      }
      return response;
    },
    [signupReq]
  );

  const saveAttributes = useCallback(async () => {
    // Opting into texts sends only the consent: the server adds text to every
    // eligible category, preserving each category's existing email state (so a
    // category the user previously turned off stays email-off), and records
    // the consent to user_history. Leaving it off sends neither.
    const body: UserAttributesUpdate = {
      ...sparkRegisterInfo,
      ...(textOptIn
        ? { notification_consent: textOptInConsent("spark-registration") }
        : {}),
    };
    if (!Object.keys(body).length) {
      return null;
    }
    const userResponse = await saveAttributeReq.put("/user/attributes", body);
    if (userResponse.success) {
      store.dispatch({
        type: ActionType.UpdateUser,
        params: { user: userResponse.result },
      });
    }
    return userResponse;
  }, [saveAttributeReq, sparkRegisterInfo, textOptIn]);

  const register = useCallback(async () => {
    setRegisterLoading(true);
    const registerPayload: {
      spark_id: number;
      coupon_code?: string;
      discount?: SparkDiscount;
    } = { spark_id: spark.id };
    if (coupon) registerPayload.coupon_code = coupon.code;
    if (activeDiscount) registerPayload.discount = activeDiscount;

    const eventID = trackMetaEvent("Purchase", {
      value: effectivePrice,
      currency: "USD",
      content_ids: [spark.id],
    });
    if (eventID) apiReq.headers["x-meta-event-id"] = eventID;

    const response = await registerReq.post("/spark/register", registerPayload);
    setRegisterLoading(false);
    if (response.success) onRegister();
  }, [activeDiscount, coupon, effectivePrice, onRegister, registerReq, spark.id]);

  const joinWaitlist = useCallback(async () => {
    const result = await waitlistReq.post("/spark/waitlist", {
      spark_id: spark.id,
    });
    if (result.success) onJoinWaitlist();
  }, [onJoinWaitlist, spark.id, waitlistReq]);

  // OTP completion. The user is now logged in; the store's `user` updates
  // during OTP verification. Re-derive missingFields from the fresh store
  // data rather than from the closure-captured `missingFields`, then advance
  // to profile or skip to checkout.
  const onOtpComplete = useCallback(async () => {
    const freshUser = store.data.user;
    const stillMissing = calculateSparkRegisterFormMissingFields(
      freshUser || {}
    );
    setStep(stillMissing.length > 0 ? "profile" : "checkout");
  }, []);

  // Profile step submission. Saves attributes, then chooses the next step
  // based on (a) whether the spark is now full for the user's gender → goal
  // flip + waitlist-offer, or (b) goal=="waitlist" already → join waitlist
  // directly, or (c) goal=="register" → checkout.
  const onProfileSubmit = useCallback(async () => {
    const result = await saveAttributes();
    if (result !== null && !result.success) return;

    const submittedGender = (
      sparkRegisterInfo.gender ||
      result?.result?.gender ||
      storeData.user?.gender
    )?.toLowerCase();

    if (goal === "waitlist") {
      // User already opted into waitlist (they entered the wizard via the
      // offer step). Just join.
      await joinWaitlist();
      return;
    }

    if (isSparkFullFor(submittedGender, spark)) {
      // Mid-flow flip: profile reveals the spark is full for the gender just
      // collected. Offer the waitlist instead of proceeding to checkout.
      setStep("waitlist-offer");
      return;
    }

    setStep("checkout");
  }, [saveAttributes, sparkRegisterInfo.gender, storeData.user?.gender, goal, spark, joinWaitlist]);

  // User accepts the mid-flow waitlist offer. Flip goal and join.
  const acceptWaitlistOffer = useCallback(async () => {
    setGoal("waitlist");
    await joinWaitlist();
  }, [joinWaitlist]);

  // Terminal action for the checkout screen. For waitlist goal (which never
  // goes through checkout — it's always handled in onProfileSubmit /
  // acceptWaitlistOffer) this is unreachable; for register goal it calls
  // register().
  const onCheckoutSubmit = useCallback(async () => {
    if (goal === "waitlist") {
      await joinWaitlist();
    } else {
      await register();
    }
  }, [goal, joinWaitlist, register]);

  return {
    view,
    loggedIn,
    gender,
    sparkRegisterInfo,
    setSparkRegisterInfo,
    textOptIn,
    setTextOptIn,
    coupon,
    setCoupon,
    registerLoading,
    activeDiscount,
    isFree,
    isEffectivelyFree,
    effectivePrice,
    missingFields,
    errors: {
      register: registerReqState.error,
      signup: signupReqState.error,
      waitlist: waitlistReqState.error,
      saveAttribute: saveAttributeReqState.error,
    },
    actions: {
      initiateLogin,
      onOtpComplete,
      onProfileSubmit,
      acceptWaitlistOffer,
      onCheckoutSubmit,
    },
  };
};
